import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { PunchCardCancellationPolicy } from '@/lib/bookingPolicies';
import type { Json } from '@/types/database.generated';

export interface PunchCardTemplate {
  id: string;
  clinic_id: string;
  name: string;
  description: string | null;
  total_clips: number;
  price_cents: number;
  practitioner_clip_value_cents?: number | null;
  currency: string;
  expires_months: number | null;
  appointment_types: Json;
  group_types: Json;
  display_order: number;
  is_active: boolean;
  visible_in_portal: boolean;
  vat_rate_percent: number;
  cancellation_policy?: PunchCardCancellationPolicy | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

// Fetch all templates for a clinic
export function usePunchCardTemplates(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['punch_card_templates', clinicId],
    queryFn: async () => {
      if (!clinicId) return [];
      
      const { data, error } = await supabase
        .from('punch_card_templates')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data as PunchCardTemplate[];
    },
    enabled: !!clinicId,
  });
}

// Fetch active templates only
export function useActivePunchCardTemplates(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['punch_card_templates', clinicId, 'active'],
    queryFn: async () => {
      if (!clinicId) return [];
      
      const { data, error } = await supabase
        .from('punch_card_templates')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data as PunchCardTemplate[];
    },
    enabled: !!clinicId,
  });
}

// Create template
export function useCreatePunchCardTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: Omit<PunchCardTemplate, 'id' | 'created_at' | 'updated_at' | 'created_by'>) => {
      const { cancellation_policy, ...rest } = template;
      const { data, error } = await supabase
        .from('punch_card_templates')
        .insert({
          ...rest,
          cancellation_policy: (cancellation_policy ?? null) as Json | null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as PunchCardTemplate;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['punch_card_templates', data.clinic_id] });
    },
  });
}

// Update template
export function useUpdatePunchCardTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PunchCardTemplate> & { id: string }) => {
      const { cancellation_policy, ...rest } = updates;
      const patch = {
        ...rest,
        ...(cancellation_policy !== undefined
          ? { cancellation_policy: (cancellation_policy ?? null) as Json | null }
          : {}),
      };
      const { data, error } = await supabase
        .from('punch_card_templates')
        .update(patch)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as PunchCardTemplate;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['punch_card_templates', data.clinic_id] });
    },
  });
}

// Delete template
export function useDeletePunchCardTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, clinicId }: { id: string; clinicId: string }) => {
      const { error } = await supabase
        .from('punch_card_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, clinicId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['punch_card_templates', data.clinicId] });
    },
  });
}

// Create punch card from template
export function useCreatePunchCardFromTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      template_id,
      patient_id,
      create_invoice = true,
    }: {
      template_id: string;
      patient_id: string;
      create_invoice?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('create_punch_card_from_template', {
        p_template_id: template_id,
        p_patient_id: patient_id,
        p_create_invoice: create_invoice,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['patient_punch_cards', variables.patient_id] });
    },
  });
}
